/**
 * Unit-aware parser for dynamic-block parameter values (D6).
 *
 * Matches AutoCAD Properties palette: accepts any configured unit,
 * converts to the drawing baseline (mm); Russian aliases
 * ("м/см/мм/дюйм/фут"); decimal comma; `±` sign with surrounding
 * whitespace. Separate angle parser handles `°`/`rad` suffix and
 * symbolic π.
 *
 *   parseParameterValue("12м", "linear")   // → 12000
 *   parseAngleValue("π/2")                  // → 90
 *   parsePointValue("100,200")              // → { x:100, y:200 }
 *
 * Returns `null` on failure — callers surface a typed feedback
 * message (`feed.dynset.parse_error_*`). Never throws.
 */

export type UnitSystem = "mm" | "cm" | "m" | "in" | "ft" | "px";

/** Conversion TO millimetres; `null` means conversion needs DPI. */
const MM_PER_UNIT: Record<UnitSystem, number | null> = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
  ft: 304.8,
  px: null,
};

/**
 * Unit token aliases — every string the user can type that should be
 * recognised as a particular `UnitSystem`. Keys are lowercased.
 * Exported so tests can assert coverage.
 */
export const UNIT_ALIASES: Readonly<Record<string, UnitSystem>> = {
  mm: "mm", мм: "mm",
  cm: "cm", см: "cm",
  m: "m", м: "m",
  metre: "m", meter: "m", meters: "m", metres: "m",
  in: "in", inch: "in", inches: "in", дюйм: "in",
  ft: "ft", foot: "ft", feet: "ft", фут: "ft",
  px: "px", pixel: "px", pixels: "px", пиксель: "px", пкс: "px",
};

function factorFor(from: UnitSystem, to: UnitSystem): number | null {
  const fromMm = MM_PER_UNIT[from];
  const toMm = MM_PER_UNIT[to];
  if (fromMm === null || toMm === null) return null;
  return fromMm / toMm;
}

/** Split `"12.5 mm"` → `{ value:"12.5", unit:"mm" }`. */
function splitValueAndUnit(raw: string): { value: string; unit: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { value: "", unit: "" };
  let i = 0;
  if (trimmed[i] === "+" || trimmed[i] === "-") i += 1;
  const numStart = i;
  while (i < trimmed.length && /[0-9]/.test(trimmed[i]!)) i += 1;
  if (i < trimmed.length && (trimmed[i] === "." || trimmed[i] === ",")) {
    i += 1;
    while (i < trimmed.length && /[0-9]/.test(trimmed[i]!)) i += 1;
  }
  if (i - numStart === 0) return { value: "", unit: trimmed };
  return {
    value: trimmed.slice(0, i),
    unit: trimmed.slice(i).trim().toLowerCase(),
  };
}

function parseNumeric(raw: string): number | null {
  if (raw.length === 0) return null;
  const normalized = raw.replace(",", ".");
  const stripped = normalized.startsWith("+")
    ? normalized.slice(1)
    : normalized;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : null;
}

function resolveUnit(token: string): UnitSystem | null {
  if (token.length === 0) return null;
  return UNIT_ALIASES[token.toLowerCase()] ?? null;
}

/**
 * Parses a linear / coord value in any recognised unit and returns
 * the result in `documentUnits`. Rotation inputs should use
 * `parseAngleValue` — this function rejects `°`/`rad` suffixes.
 */
export function parseParameterValue(
  raw: string,
  kind: "linear" | "rotation" | "coord" | "lookup",
  documentUnits: UnitSystem = "mm",
): number | null {
  if (kind === "rotation") return parseAngleValue(raw);
  if (kind === "lookup") return null;
  const { value, unit } = splitValueAndUnit(raw);
  const n = parseNumeric(value);
  if (n === null) return null;
  if (unit.length === 0) return n;
  const resolved = resolveUnit(unit);
  if (resolved === null) return null;
  const factor = factorFor(resolved, documentUnits);
  if (factor === null) return null;
  return n * factor;
}

/**
 * Parses an angle in degrees (default) or radians (`rad`/`r` suffix).
 * Accepts `"90"`, `"90°"`, `"90deg"`, `"1.5rad"`, `"π"`, `"π/2"`,
 * `"-pi*2"`. Returns null for unparseable input.
 */
export function parseAngleValue(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const piResult = tryParsePiExpression(trimmed);
  if (piResult !== null) return piResult;
  const { value, unit } = splitValueAndUnit(trimmed);
  const n = parseNumeric(value);
  if (n === null) return null;
  if (unit.length === 0) return n;
  if (
    unit === "°" ||
    unit === "deg" ||
    unit === "degree" ||
    unit === "degrees"
  ) {
    return n;
  }
  if (
    unit === "r" ||
    unit === "rad" ||
    unit === "radian" ||
    unit === "radians"
  ) {
    return (n * 180) / Math.PI;
  }
  return null;
}

/** Symbolic π: `π`, `pi`, `π/2`, `-pi*2`, `3pi/4`, `(pi/2)`. */
function tryParsePiExpression(raw: string): number | null {
  let s = raw.replace(/\s+/g, "");
  if (s.startsWith("(") && s.endsWith(")")) s = s.slice(1, -1);
  s = s.toLowerCase().replace(/pi/g, "π");
  if (!s.includes("π")) return null;
  const match = s.match(/^([+-]?)(\d*\.?\d*)π(?:([*/])(\d+\.?\d*))?$/);
  if (!match) return null;
  const sign = match[1] === "-" ? -1 : 1;
  const coef = match[2]?.length ? Number(match[2]) : 1;
  if (!Number.isFinite(coef)) return null;
  const op = match[3];
  const rhs = match[4]?.length ? Number(match[4]) : 1;
  if (!Number.isFinite(rhs)) return null;
  let radians = sign * coef * Math.PI;
  if (op === "/") radians /= rhs;
  else if (op === "*") radians *= rhs;
  return (radians * 180) / Math.PI;
}

/**
 * Parses a 2D point `"x,y"` or `"x y"`. Each coordinate may carry a
 * unit suffix; result is in `documentUnits`. Decimal-comma is
 * re-split on whitespace when the left half is a bare integer and
 * the right half starts with a digit — matches AutoCAD's
 * Russian-locale grip entry.
 */
export function parsePointValue(
  raw: string,
  documentUnits: UnitSystem = "mm",
): { x: number; y: number } | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  let partA: string;
  let partB: string;
  const sepIdx = trimmed.indexOf(",");
  if (sepIdx >= 0) {
    partA = trimmed.slice(0, sepIdx).trim();
    partB = trimmed.slice(sepIdx + 1).trim();
    if (/^[+-]?\d+$/.test(partA) && /^\d/.test(partB)) {
      const wsIdx = partB.search(/\s/);
      if (wsIdx >= 0) {
        partA = `${partA},${partB.slice(0, wsIdx).trim()}`;
        partB = partB.slice(wsIdx).trim();
      }
    }
  } else {
    const wsMatch = trimmed.match(/\s+/);
    if (!wsMatch || wsMatch.index === undefined) return null;
    partA = trimmed.slice(0, wsMatch.index).trim();
    partB = trimmed.slice(wsMatch.index).trim();
  }
  if (partA.length === 0 || partB.length === 0) return null;
  const x = parseParameterValue(partA, "linear", documentUnits);
  const y = parseParameterValue(partB, "linear", documentUnits);
  if (x === null || y === null) return null;
  return { x, y };
}
